import React, { useCallback, useContext, useEffect, useRef } from 'react'
import {
  Fields,
  Slot,
  SlotComponent,
  ComponentData,
  Data,
  renderContext,
  Render,
  usePuck,
} from '@puckeditor/core'
import PersonalityQuizStep from 'steps/personality-quiz/PersonalityQuizStep3'
import { getPuckFileUrl } from 'breaktime-theme/src/puck/fields/FileUploadField'
import {
  BackgroundStyleProps,
  backgroundStyleField,
  applyBackgroundStyle,
  applyTypography,
  borderOverlayStyle,
  splitBackgroundLayers,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import { ThemeCustomFields } from 'breaktime-theme/src/puck/theme-config'
import { cleanRichtext, richtextField } from '../ReferralCard/ReferralCard'
import { TriangleAlert } from 'lucide-react'
import { StartScreenTransition } from '../shared/StartScreen'
import { skipLogicToKeyValuePairs } from '../PQAnswer/PQAnswer'
import {
  PQ_DISALLOW,
  PQResultProps,
  pqCardStyle,
  pqResultLabel,
  pqTitleStyle,
} from '../PQResult/PQResult'
import type { PQQuestionProps } from '../PQQuestion/PQQuestion'
import type { PQAnswerProps } from '../PQAnswer/PQAnswer'

// Legacy quiz styles into the puck iframe — 1:1 editor preview.
import 'steps/shared/quiz-trivia/editorial-trivia-quiz.scss'
import 'steps/personality-quiz/quiz3.scss'
import 'steps/personality-quiz/resultSlideNewLayout.scss'
import './pq-editor.scss'

export type PQResultDisplay = 'MOST_CHOSEN' | 'DECISION_TREE'

export type PQContainerProps = {
  id?: string
  headline?: string
  /** Single Result: each answer votes for one result, most-voted wins.
   * Decision Tree: answers vote for result SETS + skip-logic gates questions
   * (legacy PersonalityQuizResultDisplay). */
  resultDisplay: PQResultDisplay
  defaultAnswerBgColor?: string
  defaultAnswerTextColor?: string
  /** Hover/selected background on answers (stylesheet var, hover needs it). */
  answerHoverColor?: string
  /** Hide the "Quiz N of M" progress bar (content hides it via css today). */
  hideProgressBar?: boolean
  /** Full-bleed step background (pushed to StepBackground, like listicle). */
  background?: BackgroundStyleProps
  /** Max width of the quiz card (`.quiz.step-content`); empty keeps the
   * theme default. Content changes this via customCss today (e.g. 900px). */
  maxWidth?: string
  /** Background of the `.quiz.step-content` section wrapper; empty keeps the
   * theme default (--step-container-background-color). */
  containerBackground?: string
  questions: Slot
  results: Slot
  startScreen: Slot
}

export const PQ_INITIAL_DATA: PQContainerProps = {
  headline: 'New Personality Quiz',
  resultDisplay: 'MOST_CHOSEN',
  questions: [],
  results: [],
  startScreen: [],
}

export const PQ_FIELDS: Fields<
  PQContainerProps,
  ThemeCustomFields[keyof ThemeCustomFields]
> = {
  headline: { type: 'text', label: 'Quiz Headline (shares & analytics)' },
  resultDisplay: {
    type: 'radio',
    label: 'Result Display',
    options: [
      { label: 'Single Result', value: 'MOST_CHOSEN' },
      { label: 'Decision Tree', value: 'DECISION_TREE' },
    ],
  },
  defaultAnswerBgColor: {
    type: 'colorPicker',
    label: 'Default Answer Background',
    clearable: true,
  },
  defaultAnswerTextColor: {
    type: 'colorPicker',
    label: 'Default Answer Text Color',
    clearable: true,
  },
  answerHoverColor: {
    type: 'colorPicker',
    label: 'Answer Hover / Selected Color',
    clearable: true,
  },
  hideProgressBar: {
    type: 'radio',
    label: 'Progress Bar',
    options: [
      { label: 'Show', value: false },
      { label: 'Hide', value: true },
    ],
  },
  background: { ...backgroundStyleField, label: 'Background' },
  maxWidth: {
    type: 'text',
    label: 'Card Max Width (empty = theme default)',
    placeholder: '900px',
  },
  containerBackground: {
    type: 'colorPicker',
    label: 'Container Background (empty = theme default)',
    clearable: true,
    alpha: true,
  },
  questions: { type: 'slot', allow: ['PQQuestion'] },
  results: { type: 'slot', allow: ['PQResult'] },
  startScreen: { type: 'slot', label: 'Start Screen', allow: ['PollStartScreen'] },
}

type PQQuestionSlotItem = {
  type: 'PQQuestion'
  props: PQQuestionProps & {
    id: string
    header?: ComponentData[]
    answers?: ComponentData[]
  }
}

type PQAnswerSlotItem = {
  type: 'PQAnswer'
  props: PQAnswerProps & { id: string; media?: ComponentData[] }
}

type PQResultSlotItem = {
  type: 'PQResult'
  props: PQResultProps & {
    id: string
    media?: ComponentData[]
    buttons?: ComponentData[]
    content?: ComponentData[]
  }
}

type PQContainerStoredProps = Omit<
  PQContainerProps,
  'questions' | 'results' | 'startScreen'
> & {
  questions?: PQQuestionSlotItem[]
  results?: PQResultSlotItem[]
  startScreen?: ComponentData[]
}

type RenderCtx = React.ContextType<typeof renderContext>

/** Callbacks + context the break player passes through PuckStep metadata. */
type PQStepMetadata = {
  brk?: { id?: string; shareUrl?: string }
  step?: { breakId?: string; [key: string]: unknown }
  onEngage?: () => void
  onAwardPoints?: () => void
  onChangeBackground?: (bg: Record<string, unknown>) => void
  skipButton?: React.ReactNode
  nextStepButton?: React.ReactNode
}

const fileUrl = (f?: unknown): string | undefined => {
  if (!f) return undefined
  if (typeof f === 'string') return f || undefined
  return getPuckFileUrl(f as Parameters<typeof getPuckFileUrl>[0]) || undefined
}

/**
 * Map puck slots onto the legacy `step.personalityQuiz` shape consumed by
 * PersonalityQuizStep3 (tally, transitions, analytics and the PQ→referral
 * contract emit all come from the legacy runtime). Ids are the puck
 * component ids — the stable identifiers the referral hide-list references.
 */
const mapToLegacyQuiz = (
  props: PQContainerStoredProps,
  slotContent: (items: ComponentData[]) => React.ReactNode,
) => {
  const tree = props.resultDisplay === 'DECISION_TREE'
  const questions = (props.questions ?? [])
    .filter(q => q.type === 'PQQuestion')
    .map((q, qi) => ({
      id: q.props.id ?? `puck-pq-q-${qi}`,
      text: cleanRichtext(q.props.text) ?? '',
      type: 'TEXT',
      image: undefined,
      // decision tree: question gate scores (filterQuestions)
      keyValuePairs: tree
        ? skipLogicToKeyValuePairs(q.props.skipLogic)
        : undefined,
      puckHeader: slotContent(q.props.header ?? []),
      puckHeaderHeight: q.props.headerHeight || undefined,
      puckMode: true,
      puckSelectedAnswerColor: q.props.selectedAnswerColor || undefined,
      puckAnswerWidth: q.props.answerWidth || undefined,
      puckAnswersStyle: (() => {
        const st = {
          ...(q.props.answersJustify
            ? { justifyContent: q.props.answersJustify }
            : {}),
          ...(q.props.answersAlign ? { alignItems: q.props.answersAlign } : {}),
          ...(q.props.answersGap ? { gap: q.props.answersGap } : {}),
        }
        return Object.keys(st).length ? st : undefined
      })(),
      puckCardStyle: pqCardStyle(q.props),
      puckCardOverlayStyle: borderOverlayStyle(q.props.borderOverlay),
      puckHeadlineStyle: {
        ...applyTypography({
          fontFamily: q.props.headlineFontFamily,
          fontFamilyFallback: q.props.headlineFontFamilyFallback,
        }),
        // sponsored scss paints the headline white; the card text color
        // must win inline when set
        ...(q.props.cardTextColor ? { color: q.props.cardTextColor } : {}),
      },
      puckBackgroundColor: q.props.backgroundColor,
      puckBackgroundImageUrl: fileUrl(q.props.backgroundImage),
      puckMobileBackgroundImageUrl: fileUrl(q.props.mobileBackgroundImage),
      answers: ((q.props.answers ?? []) as PQAnswerSlotItem[])
        .filter(a => a.type === 'PQAnswer')
        .map((a, ai) => {
          const hasMedia = !!(a.props.media ?? []).length
          return {
            id: a.props.id ?? `puck-pq-a-${qi}-${ai}`,
            text: cleanRichtext(a.props.text) ?? '',
            type: hasMedia ? 'IMAGE' : 'TEXT',
            image: undefined,
            puckMedia: hasMedia ? slotContent(a.props.media ?? []) : null,
            puckStyle: (() => {
              const backgroundColor =
                a.props.bgColor || props.defaultAnswerBgColor
              const color = a.props.textColor || props.defaultAnswerTextColor
              // question card font applied inline — the quiz scss sets an
              // explicit family on answer labels, inheritance loses
              const font = applyTypography({
                fontFamily: q.props.fontFamily,
                fontFamilyFallback: q.props.fontFamilyFallback,
              })
              const style = {
                ...(backgroundColor ? { backgroundColor } : {}),
                ...(color ? { color } : {}),
                ...font,
              }
              return Object.keys(style).length ? style : undefined
            })(),
            // calculateTally consumes resultIds (tree) or resultId (single)
            resultId: tree ? undefined : a.props.resultId || undefined,
            resultIds:
              tree && a.props.resultIds?.length ? a.props.resultIds : undefined,
            keyValuePairs: tree
              ? skipLogicToKeyValuePairs(a.props.skipLogic)
              : undefined,
          }
        }),
    }))

  const results = (props.results ?? [])
    .filter(r => r.type === 'PQResult')
    .map((r, ri) => {
      return {
      id: r.props.id ?? `puck-pq-r-${ri}`,
      title: cleanRichtext(r.props.title) ?? pqResultLabel(r.props, ri),
      desc: cleanRichtext(r.props.desc) ?? '',
      // buttons render as real components (multi ShopButton / dropdown rows,
      // legacy result-multibuttons pattern) — no legacy link mining
      link: undefined,
      linkTitle: undefined,
      // one <Render> per button so each becomes its own flex child of
      // .result-multibuttons (a single Render call wraps them all in one
      // div, collapsing the gap)
      puckButtons: (r.props.buttons ?? []).map(b =>
        slotContent([b] as ComponentData[]),
      ),
      resultLayout: r.props.resultLayout ?? 'ONE_COLUMN',
      backgroundColor: r.props.backgroundColor,
      backgroundImage: undefined,
      image: undefined,
      videoId: undefined,
      dropdownButtons: undefined,
      puckMedia: slotContent(r.props.media ?? []),
      puckContent: slotContent(r.props.content ?? []),
      puckCardStyle: {
        // when the quiz card has a custom max width the result slide must
        // stretch into it — scss caps .result_slide at max-width:750px, so
        // width:100% alone loses; clear the cap too
        ...(props.maxWidth ? { width: '100%', maxWidth: 'none' } : {}),
        ...pqCardStyle(r.props),
      },
      puckCardOverlayStyle: borderOverlayStyle(r.props.borderOverlay),
      puckDescStyle: r.props.descFontSize
        ? { fontSize: r.props.descFontSize }
        : undefined,
      puckTitleStyle: pqTitleStyle(r.props),
      puckContentStyle: r.props.contentDirection
        ? { flexDirection: r.props.contentDirection }
        : undefined,
      puckRegionStyles: {
        title:
          r.props.titleOrder != null ? { order: r.props.titleOrder } : undefined,
        desc:
          r.props.descOrder != null ? { order: r.props.descOrder } : undefined,
        media:
          r.props.mediaOrder != null ? { order: r.props.mediaOrder } : undefined,
        content:
          r.props.contentOrder != null
            ? { order: r.props.contentOrder }
            : undefined,
        buttons: {
          ...(r.props.buttonsOrder != null
            ? { order: r.props.buttonsOrder }
            : {}),
          ...(r.props.buttonsMargin ? { margin: r.props.buttonsMargin } : {}),
        },
      },
      puckBackgroundImageUrl: fileUrl(r.props.backgroundImage),
      puckMobileBackgroundImageUrl: fileUrl(r.props.mobileBackgroundImage),
      }
    })

  return {
    id: props.id,
    headline: props.headline ?? '',
    resultDisplay: props.resultDisplay ?? 'MOST_CHOSEN',
    defaultAnswerBgColor: props.defaultAnswerBgColor,
    defaultAnswerTextColor: props.defaultAnswerTextColor,
    puckHideProgressBar: props.hideProgressBar === true,
    puckAnswerHoverColor: props.answerHoverColor || undefined,
    puckMaxWidth: props.maxWidth || undefined,
    puckContainerBackground: props.containerBackground || undefined,
    questions,
    results,
  }
}

/** Production path: delegate to the extended legacy PersonalityQuizStep3. */
const PuckPQContainerRenderer = ({ puckCtx }: { puckCtx: RenderCtx }) => {
  const container = puckCtx.data.content.find(c => c.type === 'PQContainer')
  const containerProps = (container?.props ?? {}) as PQContainerStoredProps

  const {
    brk,
    step,
    onEngage,
    onAwardPoints,
    onChangeBackground,
    skipButton,
    nextStepButton,
  } = (puckCtx.metadata ?? {}) as PQStepMetadata

  const breakType: 'editorial' | 'sponsored' =
    (puckCtx.data.root?.props as { breakType?: 'editorial' | 'sponsored' })
      ?.breakType ?? 'editorial'

  const slotContent = useCallback(
    (items: ComponentData[]): React.ReactNode => {
      if (!items?.length) return null
      return (
        <Render
          config={puckCtx.config}
          data={{ content: items, root: { props: { breakType } } } as Data}
          // shareEventSlug: PollSocialShare blocks dropped in these slots
          // fire the pq-named share analytics event
          metadata={{ ...puckCtx.metadata, shareEventSlug: 'pq' }}
        />
      )
    },
    [puckCtx.config, breakType, puckCtx.metadata],
  )

  const quiz = mapToLegacyQuiz(containerProps, slotContent)
  const breakId = step?.breakId ?? brk?.id

  // Full-bleed step background — push to the outer StepBackground via
  // metadata, same contract as poll/listicle.
  const containerBackground: BackgroundStyleProps =
    containerProps.background ?? {}
  const bgStyle = applyBackgroundStyle(containerBackground)
  const hasBackground = Object.keys(bgStyle).length > 0
  const bgImageSrc = fileUrl(containerBackground.backgroundImage) ?? ''
  const mobileBgImageSrc =
    fileUrl(containerBackground.mobileBackgroundImage) ?? ''

  const onChangeBackgroundRef = useRef(onChangeBackground)
  useEffect(() => {
    onChangeBackgroundRef.current = onChangeBackground
  }, [onChangeBackground])

  useEffect(() => {
    const cb = onChangeBackgroundRef.current
    if (!cb || !hasBackground) return
    cb({
      backgroundColor: containerBackground?.backgroundColor,
      backgroundImageSrc: bgImageSrc || undefined,
      mobileBackgroundImageSrc: mobileBgImageSrc || undefined,
      backgroundSize: containerBackground?.backgroundSize,
      backgroundPosition: containerBackground?.backgroundPosition,
      backgroundRepeat: containerBackground?.backgroundRepeat,
      backgroundOpacity: containerBackground?.backgroundImageOpacity,
    })
  }, [
    hasBackground,
    containerBackground?.backgroundColor,
    bgImageSrc,
    mobileBgImageSrc,
    containerBackground?.backgroundSize,
    containerBackground?.backgroundPosition,
    containerBackground?.backgroundRepeat,
    containerBackground?.backgroundImageOpacity,
  ])

  return (
    <>
      <StartScreenTransition
        startScreen={
          containerProps.startScreen as unknown as {
            type: string
            props?: Record<string, unknown>
          }[]
        }
      >
        <PersonalityQuizStep
          brk={brk}
          breakId={breakId}
          step={{ ...(step ?? {}), personalityQuiz: quiz, socialShareOn: false }}
          onEngage={onEngage ?? (() => {})}
          onAwardPoints={onAwardPoints ?? (() => {})}
          onChangeBackground={onChangeBackground ?? (() => {})}
          skipButton={skipButton}
          nextStepButton={nextStepButton}
          isPuck
        />
      </StartScreenTransition>
    </>
  )
}

const slotLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'rgba(0,0,0,0.4)',
  margin: '12px 0 4px',
}

type PQContainerRenderProps = Omit<
  PQContainerProps,
  'questions' | 'results' | 'startScreen'
> & {
  questions: SlotComponent
  results: SlotComponent
  startScreen: SlotComponent
}

export const PuckPQContainer = (props: PQContainerRenderProps) => {
  const { questions: Questions, results: Results } = props
  const puckCtx = useContext(renderContext)
  const isRenderMode = puckCtx.data.content.length > 0

  if (isRenderMode) {
    return <PuckPQContainerRenderer puckCtx={puckCtx} />
  }

  return (
    <PQContainerEditor
      questions={Questions}
      results={Results}
      startScreen={props.startScreen}
    />
  )
}

/** Editor: questions + results stacked with labels (production plays one
 * slide at a time — this is the authoring view, like listicle's stack). */
const PQContainerEditor = ({
  questions: Questions,
  results: Results,
  startScreen: StartScreen,
}: {
  questions: SlotComponent
  results: SlotComponent
  startScreen: SlotComponent
}) => {
  const puck = usePuck()
  const isEditorial =
    puck?.appState?.data?.root?.props?.breakType === 'editorial'

  // Decision Tree validation (beaker parity): the first question's answers
  // must cover every result.
  const content = (puck?.appState?.data?.content ?? []) as ComponentData[]
  const stored = content.find(c => c.type === 'PQContainer')?.props as
    | PQContainerStoredProps
    | undefined
  let treeWarning: string | null = null
  if (stored?.resultDisplay === 'DECISION_TREE') {
    const firstQ = (stored.questions ?? [])[0]
    const covered = new Set(
      ((firstQ?.props.answers ?? []) as PQAnswerSlotItem[]).flatMap(
        a => a.props.resultIds ?? [],
      ),
    )
    const missing = (stored.results ?? [])
      .map((r, i) => ({ id: r.props.id, i }))
      .filter(r => !covered.has(r.id))
      .map(r => pqResultLabel(stored.results![r.i].props, r.i))
    if (firstQ && missing.length) {
      treeWarning = `Decision Tree: the first question's answers must cover every result. Missing: ${missing.join(', ')}`
    }
  }

  // editor preview of the container Background (production pushes it to the
  // outer StepBackground; the canvas has none)
  const bgLayers = splitBackgroundLayers(stored?.background)

  return (
    <main
      className={isEditorial ? 'editorial container' : 'sponsored'}
      style={{ display: 'flow-root', position: 'relative', ...bgLayers.base }}
    >
      {bgLayers.overlay && <div style={bgLayers.overlay} />}
      <section
        className="quiz step-content pq-editor-stack"
        style={
          {
            ...(stored?.maxWidth
              ? {
                  maxWidth: stored.maxWidth,
                  width: '100%',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  paddingLeft: 0,
                  paddingRight: 0,
                }
              : {}),
            ...(stored?.containerBackground
              ? { backgroundColor: stored.containerBackground }
              : {}),
          }
        }
      >
        {treeWarning && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              fontWeight: 600,
              color: '#8a4b08',
              background: '#fdf3e3',
              border: '1px solid #f0d9b5',
              borderRadius: 6,
              padding: '8px 12px',
              marginBottom: 12,
            }}
          >
            <TriangleAlert size={14} style={{ flex: '0 0 auto' }} />
            {treeWarning}
          </div>
        )}
        <span style={slotLabelStyle}>
          Start Screen (optional — drop a Start Screen block)
        </span>
        <StartScreen allow={['PollStartScreen']} minEmptyHeight={48} />
        <span style={slotLabelStyle}>Questions (played in order)</span>
        <Questions allow={['PQQuestion']} minEmptyHeight={80} />
        <span style={slotLabelStyle}>
          Results (add Social Share / extra blocks inside each result)
        </span>
        <Results allow={['PQResult']} minEmptyHeight={80} />
      </section>
    </main>
  )
}
